/**
 * Admin Impersonation Service
 *
 * Drives the Cognito CUSTOM_AUTH flow for admin impersonation. The backend
 * mints a short-lived HMAC artifact via `requestImpersonationToken`; this
 * service answers the resulting custom challenge with that artifact, then
 * hard-reloads so Apollo + Amplify pick up the target's session.
 *
 * Source of truth for active impersonation is the id-token's
 * `custom:impersonatedBy` claim — there is no sidecar / localStorage state.
 *
 * See: .kiro/specs/admin-impersonation/design.md → Frontend — services/impersonation.ts
 */

import { Amplify, Auth } from 'aws-amplify';
import { gql } from '@apollo/client';
import apolloClient from '../graphql/apollo-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ImpersonationArtifactResponse {
  success: boolean;
  artifact?: string;
  issuedAt?: string;
  expiresAt?: string;
  nonce?: string;
  targetUsername?: string;
  error?: string;
}

export interface ImpersonationState {
  active: true;
  actorAdminSub: string;
  targetSub: string;
  targetEmail: string;
  startedAt: string;
}

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const REQUEST_IMPERSONATION_TOKEN = gql`
  mutation RequestImpersonationToken($input: RequestImpersonationTokenInput!) {
    requestImpersonationToken(input: $input) {
      success
      artifact
      issuedAt
      expiresAt
      nonce
      targetUsername
      error
    }
  }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LOG = '[impersonation]';

/**
 * Read the current admin's sub directly from the id-token claims, synchronously
 * via the cached Amplify session. Called BEFORE Auth.signOut so we capture the
 * admin identity before the session is cleared.
 */
async function getCurrentAdminSub(): Promise<string> {
  const session = await Auth.currentSession();
  const payload = session.getIdToken().decodePayload() as Record<string, unknown>;
  const sub = payload.sub;
  if (typeof sub !== 'string' || sub.length === 0) {
    throw new Error('Failed to resolve admin sub from current session');
  }
  return sub;
}

/**
 * Decode a JWT payload (middle segment, base64url) without verification. The
 * token is already trusted — it was issued by our user pool and stored by
 * Amplify. We just need the claims.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const impersonation = {
  /**
   * Start impersonating a target user. Full flow:
   *   1. `requestImpersonationToken` mutation → artifact, issuedAt, nonce, targetUsername
   *   2. Capture current admin sub from the id-token (needed for clientMetadata)
   *   3. Sign out the admin's Amplify session (local only)
   *   4. Switch Amplify to CUSTOM_AUTH and call `Auth.signIn(targetUsername)`
   *   5. Answer the custom challenge with the artifact + clientMetadata
   *   6. Hard-reload to /dashboard so Apollo + Amplify re-read the target's token
   *
   * Throws on any backend or Cognito error; caller is expected to toast the
   * message. On throw, the admin's session remains intact (step 3 only runs
   * after a successful mutation).
   */
  async startImpersonation(targetUserId: string, targetEmail: string): Promise<void> {
    console.log(LOG, 'starting for', targetUserId, targetEmail);

    // 1. Request the one-shot artifact.
    const { data } = await apolloClient.mutate<{
      requestImpersonationToken: ImpersonationArtifactResponse;
    }>({
      mutation: REQUEST_IMPERSONATION_TOKEN,
      variables: { input: { targetUserId } },
    });

    const result = data?.requestImpersonationToken;
    if (!result || !result.success) {
      const msg = result?.error ?? 'Failed to request impersonation token';
      console.warn(LOG, 'mutation rejected:', msg);
      throw new Error(msg);
    }

    const { artifact, issuedAt, nonce, targetUsername } = result;
    if (!artifact || !issuedAt || !nonce || !targetUsername) {
      throw new Error('Impersonation token response is missing required fields');
    }

    // 2. Capture admin sub BEFORE signing out.
    const actorAdminSub = await getCurrentAdminSub();

    // 3. Drop the admin's cached session so Amplify doesn't try to re-use its
    //    tokens during the CUSTOM_AUTH sign-in.
    await Auth.signOut({ global: false });

    // 4. Flip Amplify's flow to CUSTOM_AUTH for this sign-in.
    //
    //    CRITICAL: Amplify v5 `Amplify.configure({ Auth: {...} })` replaces the
    //    entire `Auth` section rather than shallow-merging it. Passing only
    //    `{ authenticationFlowType }` would wipe `userPoolId` and
    //    `userPoolWebClientId` — the sign-in then falls back to SRP with no
    //    pool, surfacing as "Incorrect username or password". We read the
    //    current config and spread it so the override is additive.
    //
    //    The global mutation is short-lived: step 6 hard-reloads the page,
    //    which re-runs `configureAmplify()` and restores USER_SRP_AUTH.
    const currentConfig = Auth.configure() as Record<string, any>;
    Amplify.configure({
      Auth: {
        ...currentConfig,
        authenticationFlowType: 'CUSTOM_AUTH',
      },
    });

    const cognitoUser = await Auth.signIn(targetUsername);

    // 5. Answer the challenge. The clientMetadata hits CreateAuthChallenge,
    //    VerifyAuthChallengeResponse, AND PreTokenGeneration.
    await Auth.sendCustomChallengeAnswer(cognitoUser, artifact, {
      impersonatedBy: actorAdminSub,
      adminSub: actorAdminSub,
      issuedAt,
      nonce,
    });

    console.log(LOG, 'custom-auth challenge answered; reloading');

    // 6. Hard reload. Amplify has written the target's tokens to localStorage
    //    under the normal CognitoIdentityServiceProvider.<clientId>.* keys;
    //    the reload re-initialises both Apollo and Amplify against them.
    window.location.href = '/dashboard';
  },

  /**
   * Exit an impersonated session. The admin signs back in manually through the
   * normal login flow. No sidecar read, no token surgery, no localStorage
   * manipulation.
   */
  async exitImpersonation(): Promise<void> {
    console.log(LOG, 'exiting impersonation');
    await Auth.signOut({ global: false });
    window.location.href = '/login';
  },

  /**
   * Synchronous inspection of the current id-token for an active impersonation.
   * Returns null when no session exists or the token lacks the
   * `custom:impersonatedBy` claim.
   *
   * Reads directly from the Amplify-managed localStorage keys rather than
   * `Auth.currentSession()` so the banner's `useState` initialiser can resolve
   * without awaiting.
   */
  getImpersonationState(): ImpersonationState | null {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return null;

      // Amplify v5 key layout:
      //   CognitoIdentityServiceProvider.<clientId>.LastAuthUser
      //   CognitoIdentityServiceProvider.<clientId>.<user>.idToken
      const keys = Object.keys(window.localStorage);
      const lastAuthUserKey = keys.find((k) => k.endsWith('.LastAuthUser'));
      if (!lastAuthUserKey) return null;

      const lastAuthUser = window.localStorage.getItem(lastAuthUserKey);
      if (!lastAuthUser) return null;

      const prefix = lastAuthUserKey.replace(/\.LastAuthUser$/, '');
      const idToken = window.localStorage.getItem(`${prefix}.${lastAuthUser}.idToken`);
      if (!idToken) return null;

      const payload = decodeJwtPayload(idToken);
      if (!payload) return null;

      const actorAdminSub = payload['custom:impersonatedBy'];
      if (typeof actorAdminSub !== 'string' || actorAdminSub.length === 0) return null;

      const targetSub = typeof payload.sub === 'string' ? payload.sub : '';
      const targetEmail = typeof payload.email === 'string' ? payload.email : '';
      const startedAt =
        typeof payload['custom:impersonationStartedAt'] === 'string'
          ? (payload['custom:impersonationStartedAt'] as string)
          : '';

      return {
        active: true,
        actorAdminSub,
        targetSub,
        targetEmail,
        startedAt,
      };
    } catch (err) {
      console.warn(LOG, 'getImpersonationState failed:', err);
      return null;
    }
  },
};

export default impersonation;
